import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'src/store';
import { isLoggedInSelector } from 'src/store/selectors/authSelector';
import { productVersionSelector } from 'src/store/selectors/productSelector';

import { shouldShowVersionNotice } from 'src/utils/productVersion';

import { getProductInfo } from 'src/api/employerHub/product';

const VERSION_CHECK_INTERVAL = 60 * 1000; // 1 min

export const useProductVersionWatcher = () => {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const storeVersion = useSelector(productVersionSelector);
  const [isShowNotice, setIsShowNotice] = useState(false);
  const abortControllerSet = useMemo<Set<AbortController>>(() => new Set(), []);
  const baselineRef = useRef<string | null>(storeVersion ?? null);
  const latestRef = useRef<string | null>(null);
  const dismissedRef = useRef<string | null>(null);
  const isCheckingRef = useRef(false);

  const checkVersion = useCallback(async () => {
    if (document.hidden || isCheckingRef.current) return;
    isCheckingRef.current = true;
    const abortController = new AbortController();
    abortControllerSet.add(abortController);
    try {
      const { data } = await getProductInfo({ signal: abortController.signal });
      const latestVersion = data.version ?? null;
      if (!latestVersion) return;

      if (!baselineRef.current) {
        baselineRef.current = latestVersion;
        return;
      }

      latestRef.current = latestVersion;
      const shouldShow = shouldShowVersionNotice({
        baselineVersion: baselineRef.current,
        latestVersion,
        dismissedVersion: dismissedRef.current,
      });
      if (shouldShow) setIsShowNotice(true);
    } catch {
      // Ignore failures and cancellations: retry on the next tick
    } finally {
      abortControllerSet.delete(abortController);
      isCheckingRef.current = false;
    }
  }, [abortControllerSet]);

  const dismiss = useCallback(() => {
    dismissedRef.current = latestRef.current;
    setIsShowNotice(false);
  }, []);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  const cleanup = useCallback(() => {
    abortControllerSet.forEach((controller) => controller.abort());
    abortControllerSet.clear();
  }, [abortControllerSet]);

  // The startup fetch may complete after mount; use it as the baseline when there is none yet
  useEffect(() => {
    if (!baselineRef.current && storeVersion) baselineRef.current = storeVersion;
  }, [storeVersion]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const intervalId = window.setInterval(checkVersion, VERSION_CHECK_INTERVAL);
    const onVisibilityChange = () => {
      if (!document.hidden) checkVersion();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isLoggedIn, checkVersion]);

  useEffect(() => () => cleanup(), [cleanup]);

  return { isShowNotice: isLoggedIn && isShowNotice, dismiss, reload };
};
